'use client'

import React from 'react'
import { CollectionArchive } from '@/ui/components/CollectionArchive'
import { CardType } from '@/ui/components/Card/types'
import type { BaseGatedArchiveClientProps } from '@/ui/blocks/shared/gatedArchiveTypes'
import { SortingOption } from '@/ui/components/SortBy'

/**
 * Props for ListedProductsSolutionsClient component
 */
export interface ListedProductsSolutionsClientProps extends Omit<BaseGatedArchiveClientProps, 'title' | 'introContent' | 'titleHierarchy' | 'fixedTitleStyle'> {
  /** Collection type for fetching correct data */
  collectionType: 'products' | 'solutions'
  /** Sorting options */
  sortingOptions?: SortingOption[]
  /** Hide when empty */
  hideWhenEmpty?: boolean
}

/**
 * Client component for ListedProductsSolutions
 *
 * Displays products or solutions (both stored in products collection).
 * All gating, authentication, and rendering logic is handled by CollectionArchive.
 * Title/intro rendering is handled by ArchiveTitleSection in the server component.
 * Creates a wrapper function client-side to include collectionType in the server action call.
 */
export const ListedProductsSolutionsClient: React.FC<ListedProductsSolutionsClientProps> = ({
  initialData,
  fetchCollectionDataAction,
  isGated,
  gateConfig,
  where,
  locale,
  limit,
  gridCols,
  archiveId,
  emptyStateType,
  emptyStateButton,
  emptyStateHasButton,
  collectionType,
  sortingOptions,
  hideWhenEmpty = false,
}) => {
  // Determine card type based on collection type
  const cardType = collectionType === 'solutions' ? CardType.Teaser : CardType.StandardFavorite

  // Create wrapper function client-side that includes collectionType
  const fetchDataWithCollectionType = React.useCallback(
    async (params: any) => {
      return fetchCollectionDataAction({
        ...params,
        collectionType,
      } as any)
    },
    [fetchCollectionDataAction, collectionType]
  )

  // If hideWhenEmpty is true and no docs, return null
  if (hideWhenEmpty && initialData.totalDocs === 0) {
    return null
  }

  return (
    <CollectionArchive
      archiveId={archiveId}
      initialData={initialData}
      fetchCollectionDataAction={fetchDataWithCollectionType}
      collection="products"
      locale={locale}
      where={where}
      limit={limit}
      gridCols={gridCols}
      cardType={cardType}
      loadMoreStrategy="none"
      sortingOptions={sortingOptions}
      // Gating props
      isGated={isGated}
      gateConfig={gateConfig}
      // Empty state props
      emptyStateType={emptyStateType}
      emptyStateButton={emptyStateButton}
      emptyStateHasButton={emptyStateHasButton}
      // Tracking
      enableTracking={true}
    />
  )
}
